import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  limit,
  doc,
  getDocs,
} from "firebase/firestore";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { dbFinancialPromotions } from "../../../backend/firebaseConfig";
import { useAdviserListWithUnappointed } from "../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";

import "../../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../../css/Marketing/FinancialPromotions/Dashboard/AdminApprovals.css";

import SearchField from "./buttons/SearchField";
import RiskPill from "./buttons/RiskPill";
import StatusTag from "./buttons/StatusTag";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import Pagination from "../../DesignSystem/Pagination/Pagination";

import { extractNameFromEmail } from "./utils/Utils";
import { RENEWAL_NOTICE_MONTHS, RENEWAL_NOTICE_MESSAGE } from "./utils/renewalConfig";
import Banner from "../../DesignSystem/Banner/Banner";

import { ReactComponent as FilterIcon } from "../../../assets/icons/financialPromotions/Filter.svg";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

const SUBMISSIONS_SUBCOL = "Submissions";
const ATTEMPTS_SUBCOL = "attempts";

const ROWS_PER_PAGE = 15;
const TABLE_ATTEMPTS_LIMIT = 5;
const STATS_ATTEMPTS_LIMIT = 50;
const OVERVIEW_BATCH_SIZE = 25;

const WORK_START_HOUR = 8.5;
const WORK_END_HOUR = 17;
const WORKING_DAY_MS = (WORK_END_HOUR - WORK_START_HOUR) * 60 * 60 * 1000;

const DEFAULT_STATS_SORT_CONFIG = {
  key: "adviser",
  direction: "asc",
};

const getDetailsKey = (submission) =>
  `${submission?.userEmail || "unknown"}-${submission?.id || ""}`;

const toJsDate = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === "number") {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  return null;
};

const toEpoch = (value) => {
  const date = toJsDate(value);
  return date ? date.getTime() : 0;
};

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const isApprovedForRenewal = (submission) =>
  normalizeStatus(
    submission?.decision || submission?.latestDecision ||
    submission?.status || submission?.latestStatus,
  ) === "approved";

const isStaffSubmission = (submission) => {
  return String(submission?.userEmail || "")
    .trim()
    .toLowerCase()
    .endsWith("@rosemountfs.co.uk");
};

const statusMatchesTab = (status, tabKey) => {
  const normalized = normalizeStatus(status);

  if (tabKey === "stats") return true;

  if (tabKey === "new") {
    return [
      "awaiting_review",
      "submitted",
      "new",
      "processing",
      "processiong",
    ].includes(normalized);
  }

  if (tabKey === "progress") {
    return [
      "in_progress",
      "in review",
      "in_review",
      "action_required",
      "requested_changes",
    ].includes(normalized);
  }

  return ["completed", "approved", "failed", "checked", "declined"].includes(
    normalized,
  );
};

const submissionMatchesTab = (submission, tabKey) => {
  if (tabKey === "archive") return submission.isArchived;
  if (submission.isArchived) return false;
  if (tabKey === "renewals") {
    if (!isApprovedForRenewal(submission)) return false;
    const expiry = toJsDate(submission.renewalExpiryDate);
    return Boolean(expiry) && !dayjs().startOf("day").isBefore(
      dayjs(expiry).subtract(RENEWAL_NOTICE_MONTHS, "month").startOf("day"),
    );
  }
  if (tabKey === "stats") return !isStaffSubmission(submission);

  const effectiveStatus = getEffectiveStatus(submission, null);

  if (tabKey === "staff") {
    if (!isStaffSubmission(submission)) return false;

    return ![
      "completed",
      "approved",
      "checked",
      "declined",
      "rejected",
      "failed",
    ].includes(normalizeStatus(effectiveStatus));
  }

  if (tabKey === "new" && isStaffSubmission(submission)) return false;

  return statusMatchesTab(effectiveStatus, tabKey);
};

const getEffectiveStatus = (submission, latestAttempt = null) => {
  return (
    submission?.status ||
    submission?.decision ||
    submission?.latestStatus ||
    submission?.latestDecision ||
    latestAttempt?.status ||
    latestAttempt?.decision ||
    ""
  );
};

const getSubmittedDateValue = (submission, attempt) => {
  return (
    attempt?.submittedAt ||
    attempt?.createdAt ||
    submission?.createdAt ||
    attempt?.updatedAt ||
    submission?.updatedAt ||
    null
  );
};

const getStaffResponseDateValue = (submission, attempt) => {
  return (
    attempt?.firstStaffResponseAt ||
    submission?.firstStaffResponseAt ||
    attempt?.reviewedAt ||
    attempt?.approvedAt ||
    attempt?.declinedAt ||
    attempt?.requestedAt ||
    submission?.reviewedAt ||
    submission?.approvedAt ||
    submission?.declinedAt ||
    submission?.requestedAt ||
    null
  );
};

const hasStaffResponse = (submission, attempt) => {
  const staffEmail =
    attempt?.firstStaffResponseBy ||
    attempt?.reviewedByEmail ||
    attempt?.approvedByEmail ||
    attempt?.declinedByEmail ||
    attempt?.requestedByEmail ||
    submission?.firstStaffResponseBy ||
    submission?.reviewedByEmail ||
    submission?.approvedByEmail ||
    submission?.declinedByEmail ||
    submission?.requestedByEmail ||
    "";

  return Boolean(String(staffEmail).trim());
};

const formatSubmittedDateTime = (dateValue) => {
  const jsDate = toJsDate(dateValue);
  if (!jsDate) return "-";
  return dayjs(jsDate).format("D MMM YYYY, h:mm A");
};

const setTimeFromDecimalHour = (date, decimalHour) => {
  const next = new Date(date);
  const hours = Math.floor(decimalHour);
  const minutes = Math.round((decimalHour - hours) * 60);

  next.setHours(hours, minutes, 0, 0);
  return next;
};

const isWorkingDay = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const getWorkingDurationMs = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  if (endDate <= startDate) return 0;

  let totalMs = 0;
  const cursor = new Date(startDate);

  while (cursor < endDate) {
    if (isWorkingDay(cursor)) {
      const workStart = setTimeFromDecimalHour(cursor, WORK_START_HOUR);
      const workEnd = setTimeFromDecimalHour(cursor, WORK_END_HOUR);

      const rangeStart = new Date(
        Math.max(cursor.getTime(), workStart.getTime()),
      );

      const rangeEnd = new Date(Math.min(endDate.getTime(), workEnd.getTime()));

      if (rangeEnd > rangeStart) {
        totalMs += rangeEnd.getTime() - rangeStart.getTime();
      }
    }

    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(0, 0, 0, 0);
  }

  return totalMs;
};

const formatAverageResponseTime = (milliseconds) => {
  if (!milliseconds || milliseconds <= 0) return "-";

  const minutes = Math.round(milliseconds / 60000);
  const hours = milliseconds / 3600000;
  const workingDays = milliseconds / WORKING_DAY_MS;

  if (minutes < 60) return `${minutes} min`;

  if (hours < 8.5) {
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hr`;
  }

  const roundedDays = Math.round(workingDays * 10) / 10;
  return `${roundedDays} working day${roundedDays === 1 ? "" : "s"}`;
};

const buildAttemptRows = (attemptsSnap) => {
  const attemptsRows = attemptsSnap.docs.map((attemptDoc) => {
    const attemptData = attemptDoc.data() || {};

    return {
      id: attemptDoc.id,
      ref: attemptDoc.ref,
      ...attemptData,
      media: Array.isArray(attemptData.media) ? attemptData.media : [],
      filesMeta: Array.isArray(attemptData?.input?.filesMeta)
        ? attemptData.input.filesMeta
        : [],
    };
  });

  attemptsRows.sort((a, b) => {
    const aTime = Math.max(
      toEpoch(a?.updatedAt),
      toEpoch(a?.completedAt),
      toEpoch(a?.createdAt),
    );

    const bTime = Math.max(
      toEpoch(b?.updatedAt),
      toEpoch(b?.completedAt),
      toEpoch(b?.createdAt),
    );

    return bTime - aTime;
  });

  return attemptsRows;
};

const chunkArray = (array, size) => {
  const chunks = [];

  for (let index = 0; index < array.length; index += size) {
    chunks.push(array.slice(index, index + size));
  }

  return chunks;
};

export default function AdminApprovals() {
  const navigate = useNavigate();

  const [rawSubmissions, setSubmissions] = useState([]);
  const [renewalExpiryDates, setRenewalExpiryDates] = useState({});
  const { advisers } = useAdviserListWithUnappointed();
  const submissions = useMemo(() => {
    const archivedAdviserEmails = new Set(
      advisers
        .filter((adviser) => adviser.isAppointed === false)
        .flatMap((adviser) => [adviser.email, adviser.adviserId])
        .filter(Boolean)
        .map((email) => String(email).trim().toLowerCase()),
    );

    return rawSubmissions.map((submission) => ({
      ...submission,
      renewalExpiryDate: renewalExpiryDates[getDetailsKey(submission)]?.expiryDate,
      renewalApprovedByEmail: renewalExpiryDates[getDetailsKey(submission)]?.approvedByEmail,
      isArchived: archivedAdviserEmails.has(
        String(submission.userEmail || "").trim().toLowerCase(),
      ),
    }));
  }, [rawSubmissions, advisers, renewalExpiryDates]);

  useEffect(() => {
    let cancelled = false;
    const loadRenewalDates = async () => {
      const dates = {};
      const approvedSubmissions = rawSubmissions.filter(isApprovedForRenewal);
      for (const batch of chunkArray(approvedSubmissions, OVERVIEW_BATCH_SIZE)) {
        if (cancelled) return;
        await Promise.all(batch.map(async (submission) => {
          const savedExpiryDate = toJsDate(submission.expiryDate);
          if (savedExpiryDate) {
            dates[getDetailsKey(submission)] = {
              expiryDate: savedExpiryDate,
              approvedByEmail: submission.approvedByEmail || submission.reviewedByEmail || "",
            };
            return;
          }
          let approvalDate = toJsDate(submission.approvedAt);
          let approvedByEmail = submission.approvedByEmail || submission.reviewedByEmail || "";
          try {
            const snapshot = await getDocs(query(
              collection(submission.ref, ATTEMPTS_SUBCOL),
              orderBy("createdAt", "desc"),
              limit(STATS_ATTEMPTS_LIMIT),
            ));
            const approvedAttempt = buildAttemptRows(snapshot).find(isApprovedForRenewal);
            approvedByEmail = approvedAttempt?.approvedByEmail ||
              approvedAttempt?.reviewedByEmail || approvedByEmail;
            approvalDate = toJsDate(
              approvedAttempt?.approvedAt || approvedAttempt?.reviewedAt ||
              approvedAttempt?.updatedAt || approvedAttempt?.completedAt || submission.approvedAt,
            );
          } catch (error) {
            console.error("Failed to load renewal approval date:", error);
          }
          if (approvalDate) {
            const expiry = new Date(approvalDate);
            expiry.setFullYear(expiry.getFullYear() + 1);
            dates[getDetailsKey(submission)] = { expiryDate: expiry, approvedByEmail };
          }
        }));
      }
      if (!cancelled) setRenewalExpiryDates(dates);
    };
    loadRenewalDates();
    return () => { cancelled = true; };
  }, [rawSubmissions]);
  const [submissionDetailsById, setSubmissionDetailsById] = useState({});
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [statsSortConfig, setStatsSortConfig] = useState(
    DEFAULT_STATS_SORT_CONFIG,
  );

  const [activeTab, setActiveTab] = useState("new");

  const [searchTerm, setSearchTerm] = useState(
    () => sessionStorage.getItem("fpAdminApprovalsSearchTerm") || "",
  );

  const [statsSearchTerm, setStatsSearchTerm] = useState(
    () => sessionStorage.getItem("fpAdminStatsSearchTerm") || "",
  );

  const [page, setPage] = useState(
    () => Number(sessionStorage.getItem("fpAdminApprovalsPage")) || 1,
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    sessionStorage.setItem("fpAdminStatsSearchTerm", statsSearchTerm);
  }, [statsSearchTerm]);

  useEffect(() => {
    sessionStorage.setItem("fpAdminApprovalsSearchTerm", searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    sessionStorage.setItem("fpAdminApprovalsPage", String(page));
  }, [page]);

  useEffect(() => {
    const submissionsQuery = query(
      collectionGroup(dbFinancialPromotions, SUBMISSIONS_SUBCOL),
      orderBy("createdAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      submissionsQuery,
      (snap) => {
        const rows = snap.docs.map((snapshotDoc) => {
          const userEmail = snapshotDoc.ref.parent?.parent?.id || null;

          return {
            id: snapshotDoc.id,
            ref: snapshotDoc.ref,
            userEmail,
            ...snapshotDoc.data(),
          };
        });

        setSubmissions(rows);
      },
      (error) => {
        console.error("Submissions snapshot error:", error);
      },
    );

    return () => unsubscribe();
  }, []);

  const userPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userPermissions")) || [];
    } catch {
      return [];
    }
  }, []);

  const canViewStats = userPermissions.some((permission) =>
    String(permission || "")
      .trim()
      .toLowerCase()
      .includes("administrator"),
  );

  const submissionTabCounts = useMemo(
    () => ({
      new: submissions.filter((submission) =>
        submissionMatchesTab(submission, "new"),
      ).length,
      staff: submissions.filter((submission) =>
        submissionMatchesTab(submission, "staff"),
      ).length,
      progress: submissions.filter((submission) =>
        submissionMatchesTab(submission, "progress"),
      ).length,
      completed: submissions.filter((submission) =>
        submissionMatchesTab(submission, "completed"),
      ).length,
      stats: submissions.filter((submission) =>
        submissionMatchesTab(submission, "stats"),
      ).length,
      archive: submissions.filter((submission) =>
        submissionMatchesTab(submission, "archive"),
      ).length,
      renewals: submissions.filter((submission) =>
        submissionMatchesTab(submission, "renewals"),
      ).length,
    }),
    [submissions],
  );

  const submissionTabs = useMemo(
    () => [
      { id: "new", label: "New Submissions", count: submissionTabCounts.new },
      {
        id: "staff",
        label: "Marketing Submissions",
        count: submissionTabCounts.staff,
      },
      { id: "renewals", label: "Renewals", count: submissionTabCounts.renewals },
      {
        id: "progress",
        label: "In Progress",
        count: submissionTabCounts.progress,
      },
      {
        id: "completed",
        label: "Completed Submissions",
        count: submissionTabCounts.completed,
      },
      ...(canViewStats
        ? [
            {
              id: "stats",
              label: "Overview",
              count: submissionTabCounts.stats,
            },
          ]
        : []),
      { id: "archive", label: "Archive", count: submissionTabCounts.archive },
    ],
    [canViewStats, submissionTabCounts],
  );

  const filteredSubmissions = useMemo(() => {
    if (activeTab === "stats") return [];

    return submissions.filter((submission) => {
      if (!submissionMatchesTab(submission, activeTab)) return false;

      if (!searchTerm.trim()) return true;

      const queryText = searchTerm.trim().toLowerCase();

      const searchable = [
        submission?.createdByEmail,
        submission?.userEmail,
        submission?.adviserDetails,
        submission?.adviserName,
        submission?.companyName,
        submission?.firmName,
        submission?.title,
        submission?.contentType,
        submission?.mediaType,
        submission?.reference,
        submission?.submissionReference,
        submission?.id,
        submission?.latestMediaTypes,
        submission?.latestStatus,
        submission?.latestDecision,
        submission?.latestRisk,
      ]
        .flat()
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(queryText);
    });
  }, [activeTab, searchTerm, submissions]);

  const sortedSubmissions = useMemo(() => {
    if (activeTab === "renewals") {
      return [...filteredSubmissions].sort((a, b) =>
        toEpoch(a.renewalExpiryDate) - toEpoch(b.renewalExpiryDate),
      );
    }
    if (activeTab !== "completed") return filteredSubmissions;

    return [...filteredSubmissions].sort((a, b) => {
      const aApprovalDateValue =
        a?.approvedAt ||
        a?.reviewedAt ||
        a?.completedAt ||
        a?.updatedAt ||
        a?.createdAt;

      const bApprovalDateValue =
        b?.approvedAt ||
        b?.reviewedAt ||
        b?.completedAt ||
        b?.updatedAt ||
        b?.createdAt;

      const aApprovalEpoch = toJsDate(aApprovalDateValue)?.getTime?.() || 0;
      const bApprovalEpoch = toJsDate(bApprovalDateValue)?.getTime?.() || 0;

      return bApprovalEpoch - aApprovalEpoch;
    });
  }, [activeTab, filteredSubmissions]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedSubmissions.length / ROWS_PER_PAGE),
  );

  const safePage = Math.min(page, totalPages);

  const paginatedSubmissions = useMemo(() => {
    return sortedSubmissions.slice(
      (safePage - 1) * ROWS_PER_PAGE,
      safePage * ROWS_PER_PAGE,
    );
  }, [safePage, sortedSubmissions]);

  const visibleSubmissionKey = useMemo(() => {
    return paginatedSubmissions.map(getDetailsKey).join("|");
  }, [paginatedSubmissions]);

  const overviewSubmissionKey = useMemo(() => {
    if (activeTab !== "stats") return "";
    return submissions.map(getDetailsKey).join("|");
  }, [activeTab, submissions]);

  useEffect(() => {
    if (!submissions.length) {
      setSubmissionDetailsById({});
      setOverviewLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadOverviewDetails = async () => {
      setOverviewLoading(true);

      const nextDetails = {};
      const batches = chunkArray(submissions, OVERVIEW_BATCH_SIZE);

      for (const batch of batches) {
        if (cancelled) return;

        await Promise.all(
          batch.map(async (submission) => {
            const detailsKey = getDetailsKey(submission);

            try {
              const attemptsRef = collection(submission.ref, ATTEMPTS_SUBCOL);

              const attemptsQuery = query(
                attemptsRef,
                orderBy("createdAt", "desc"),
                limit(STATS_ATTEMPTS_LIMIT),
              );

              const attemptsSnap = await getDocs(attemptsQuery);
              const attemptsRows = buildAttemptRows(attemptsSnap);
              const latestAttempt = attemptsRows[0] || null;

              const mediaTypes = Array.isArray(latestAttempt?.input?.mediaTypes)
                ? latestAttempt.input.mediaTypes
                : [];

              nextDetails[detailsKey] = {
                latestAttempt,
                mediaTypes,
                allAttempts: attemptsRows,
                latestAttemptAiRisk: null,
              };
            } catch {
              nextDetails[detailsKey] = {
                latestAttempt: null,
                mediaTypes: [],
                allAttempts: [],
                latestAttemptAiRisk: null,
              };
            }
          }),
        );

        if (!cancelled) {
          setSubmissionDetailsById((prev) => ({
            ...prev,
            ...nextDetails,
          }));
        }
      }

      if (!cancelled) {
        setOverviewLoading(false);
      }
    };

    if (activeTab === "stats") {
      loadOverviewDetails();

      return () => {
        cancelled = true;
        setOverviewLoading(false);
      };
    }

    setOverviewLoading(false);

    const visibleSubmissions = paginatedSubmissions;

    if (!visibleSubmissions.length) {
      setSubmissionDetailsById({});
      return undefined;
    }

    const visibleKeys = new Set(visibleSubmissions.map(getDetailsKey));

    setSubmissionDetailsById((prev) => {
      const next = {};

      visibleKeys.forEach((key) => {
        if (prev[key]) next[key] = prev[key];
      });

      return next;
    });

    const aiUnsubsByKey = new Map();
    const combinedUnsubs = [];

    visibleSubmissions.forEach((submission) => {
      const detailsKey = getDetailsKey(submission);
      let latestAttemptId = null;

      const attemptsRef = collection(submission.ref, ATTEMPTS_SUBCOL);

      const attemptsQuery = query(
        attemptsRef,
        orderBy("createdAt", "desc"),
        limit(TABLE_ATTEMPTS_LIMIT),
      );

      const unsubAttempts = onSnapshot(
        attemptsQuery,
        (attemptsSnap) => {
          const attemptsRows = buildAttemptRows(attemptsSnap);
          const latestAttempt = attemptsRows[0] || null;

          const mediaTypes = Array.isArray(latestAttempt?.input?.mediaTypes)
            ? latestAttempt.input.mediaTypes
            : [];

          setSubmissionDetailsById((prev) => ({
            ...prev,
            [detailsKey]: {
              ...(prev[detailsKey] || {}),
              latestAttempt,
              mediaTypes,
              allAttempts: attemptsRows,
            },
          }));

          const nextLatestAttemptId = latestAttempt?.id || null;

          if (nextLatestAttemptId === latestAttemptId) return;

          latestAttemptId = nextLatestAttemptId;

          const existingAiUnsub = aiUnsubsByKey.get(detailsKey);

          if (existingAiUnsub) {
            existingAiUnsub();
            aiUnsubsByKey.delete(detailsKey);
          }

          if (!latestAttempt?.ref) {
            setSubmissionDetailsById((prev) => ({
              ...prev,
              [detailsKey]: {
                ...(prev[detailsKey] || {}),
                latestAttemptAiRisk: null,
              },
            }));

            return;
          }

          const aiRef = doc(latestAttempt.ref, "private", "ai");

          const unsubAi = onSnapshot(
            aiRef,
            (aiSnap) => {
              const aiData = aiSnap.exists() ? aiSnap.data() : null;
              const aiResult = aiData?.result || null;
              const aiAnalysis = aiResult?.analysis || null;

              const latestAttemptAiRisk =
                aiAnalysis?.risk || aiResult?.risk || aiData?.risk || null;

              setSubmissionDetailsById((prev) => ({
                ...prev,
                [detailsKey]: {
                  ...(prev[detailsKey] || {}),
                  latestAttemptAiRisk,
                },
              }));
            },
            () => {
              setSubmissionDetailsById((prev) => ({
                ...prev,
                [detailsKey]: {
                  ...(prev[detailsKey] || {}),
                  latestAttemptAiRisk: null,
                },
              }));
            },
          );

          aiUnsubsByKey.set(detailsKey, unsubAi);
        },
        () => {
          setSubmissionDetailsById((prev) => ({
            ...prev,
            [detailsKey]: {
              latestAttempt: null,
              mediaTypes: [],
              latestAttemptAiRisk: null,
              allAttempts: [],
            },
          }));
        },
      );

      combinedUnsubs.push(() => {
        unsubAttempts();

        const unsubscribeAi = aiUnsubsByKey.get(detailsKey);

        if (unsubscribeAi) {
          unsubscribeAi();
          aiUnsubsByKey.delete(detailsKey);
        }
      });
    });

    return () => {
      cancelled = true;

      combinedUnsubs.forEach((unsubscribe) => unsubscribe && unsubscribe());

      aiUnsubsByKey.forEach((unsubscribe) => unsubscribe && unsubscribe());
      aiUnsubsByKey.clear();
    };
  }, [
    activeTab,
    overviewSubmissionKey,
    paginatedSubmissions,
    submissions,
    visibleSubmissionKey,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setPage(1);
  }, [activeTab, searchTerm]);

  const getRiskLabel = (submission) => {
    const detailsKey = getDetailsKey(submission);
    const details = submissionDetailsById?.[detailsKey] || {};
    const latestAttempt = details.latestAttempt;

    const rawRisk =
      details?.latestAttemptAiRisk ||
      submission?.riskDetected ||
      submission?.risk ||
      submission?.riskLevel ||
      latestAttempt?.riskDetected ||
      latestAttempt?.risk ||
      submission?.latestRisk ||
      "Unknown";

    const normalized = String(rawRisk).toUpperCase();

    if (
      ["HIGH", "MEDIUM", "MED", "LOW", "PROCESSING", "UNKNOWN"].includes(
        normalized,
      )
    ) {
      if (normalized === "MED") return "Medium";
      return normalized.charAt(0) + normalized.slice(1).toLowerCase();
    }

    return "Unknown";
  };

  const getCompletedDecisionLabel = (submission, latestAttempt) => {
    const raw = String(
      submission?.decision ||
        submission?.latestDecision ||
        latestAttempt?.decision ||
        submission?.status ||
        submission?.latestStatus ||
        latestAttempt?.status ||
        "",
    )
      .trim()
      .toLowerCase();

    if (["declined", "rejected", "failed"].includes(raw)) return "Declined";
    if (["approved", "completed", "checked"].includes(raw)) return "Approved";

    return "-";
  };

  const getStatsSortValue = (row, key) => {
    if (key === "adviser") return row.adviserName || row.adviserEmail || "";
    if (key === "total") return Number(row.total || 0);
    if (key === "newCount") return Number(row.newCount || 0);
    if (key === "inProgressCount") return Number(row.inProgressCount || 0);
    if (key === "approvedCount") return Number(row.approvedCount || 0);
    if (key === "declinedCount") return Number(row.declinedCount || 0);
    if (key === "staffResponses") return Number(row.staffResponses || 0);
    if (key === "averageResponseMs") return Number(row.averageResponseMs || 0);

    return "";
  };

  const sortStatsRows = (rows = [], sortConfig) => {
    const { key, direction } = sortConfig;

    return [...rows].sort((a, b) => {
      const aValue = getStatsSortValue(a, key);
      const bValue = getStatsSortValue(b, key);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      const result = String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return direction === "asc" ? result : -result;
    });
  };

  const handleStatsSort = (key) => {
    setStatsSortConfig((prev) => {
      if (prev.key !== key) {
        return {
          key,
          direction: "asc",
        };
      }

      if (prev.direction === "asc") {
        return {
          key,
          direction: "desc",
        };
      }

      return DEFAULT_STATS_SORT_CONFIG;
    });
  };

  const resetStatsSort = () => {
    setStatsSortConfig(DEFAULT_STATS_SORT_CONFIG);
  };

  const StatsSortableHeader = ({ label, sortKey }) => {
    const isActive = statsSortConfig.key === sortKey;

    return (
      <button
        type="button"
        className={`fp-stats-header-button ${
          isActive ? "fp-stats-header-button--active" : ""
        }`}
        onClick={() => handleStatsSort(sortKey)}
      >
        <span>{label}</span>

        <span
          className={`fp-stats-sort-icon ${
            isActive && statsSortConfig.direction === "desc"
              ? "fp-stats-sort-icon--desc"
              : ""
          }`}
        >
          <SortIcon />
        </span>
      </button>
    );
  };

  const adviserStats = useMemo(() => {
    const statsMap = new Map();

    submissions.forEach((submission) => {
      if (!submissionMatchesTab(submission, "stats")) return;

      const detailsKey = getDetailsKey(submission);
      const details = submissionDetailsById?.[detailsKey] || {};
      const latestAttempt = details.latestAttempt || null;

      const allAttempts = Array.isArray(details.allAttempts)
        ? details.allAttempts
        : latestAttempt
          ? [latestAttempt]
          : [];

      const adviserMeta = latestAttempt?.input?.adviserMeta || {};

      const adviserEmail = String(
        adviserMeta?.email ||
          submission?.createdByEmail ||
          submission?.userEmail ||
          "unknown",
      )
        .trim()
        .toLowerCase();

      const adviserName =
        adviserMeta?.tradingName ||
        adviserMeta?.tradingStyle ||
        [adviserMeta?.firstName, adviserMeta?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        submission?.adviserName ||
        submission?.companyName ||
        submission?.firmName ||
        extractNameFromEmail(adviserEmail) ||
        "Unknown Adviser";

      if (!statsMap.has(adviserEmail)) {
        statsMap.set(adviserEmail, {
          adviserEmail,
          adviserName,
          total: 0,
          newCount: 0,
          inProgressCount: 0,
          approvedCount: 0,
          declinedCount: 0,
          staffResponses: 0,
          responseTotalMs: 0,
        });
      }

      const current = statsMap.get(adviserEmail);

      const effectiveStatus = normalizeStatus(
        getEffectiveStatus(submission, latestAttempt),
      );

      current.total += 1;

      if (
        [
          "awaiting_review",
          "submitted",
          "new",
          "processing",
          "processiong",
        ].includes(effectiveStatus)
      ) {
        current.newCount += 1;
      }

      if (
        [
          "in_progress",
          "in review",
          "in_review",
          "action_required",
          "requested_changes",
        ].includes(effectiveStatus)
      ) {
        current.inProgressCount += 1;
      }

      if (["approved", "completed", "checked"].includes(effectiveStatus)) {
        current.approvedCount += 1;
      }

      if (["declined", "rejected", "failed"].includes(effectiveStatus)) {
        current.declinedCount += 1;
      }

      allAttempts.forEach((attempt) => {
        const storedDurationMs = Number(attempt?.staffResponseDurationMs || 0);

        if (storedDurationMs > 0) {
          current.staffResponses += 1;
          current.responseTotalMs += storedDurationMs;
          return;
        }

        if (!hasStaffResponse(submission, attempt)) return;

        const submittedDate = toJsDate(
          getSubmittedDateValue(submission, attempt),
        );

        const responseDate = toJsDate(
          getStaffResponseDateValue(submission, attempt),
        );

        if (!submittedDate || !responseDate) return;

        const responseMs = getWorkingDurationMs(submittedDate, responseDate);

        if (responseMs <= 0) return;

        current.staffResponses += 1;
        current.responseTotalMs += responseMs;
      });
    });

    return Array.from(statsMap.values()).map((item) => ({
      ...item,
      completedCount: item.approvedCount + item.declinedCount,
      averageResponseMs:
        item.staffResponses > 0
          ? item.responseTotalMs / item.staffResponses
          : 0,
    }));
  }, [submissionDetailsById, submissions]);

  const sortedAdviserStats = useMemo(() => {
    const sortedRows = sortStatsRows(adviserStats, statsSortConfig);
    const queryText = statsSearchTerm.trim().toLowerCase();

    if (!queryText) return sortedRows;

    return sortedRows.filter((row) => {
      const searchable = [
        row.adviserName,
        row.adviserEmail,
        row.total,
        row.newCount,
        row.inProgressCount,
        row.approvedCount,
        row.declinedCount,
        row.staffResponses,
        formatAverageResponseTime(row.averageResponseMs),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(queryText);
    });
  }, [adviserStats, statsSearchTerm, statsSortConfig]);

  const statsTotals = useMemo(() => {
    const totals = adviserStats.reduce(
      (acc, row) => {
        acc.advisers += 1;
        acc.total += row.total || 0;
        acc.newCount += row.newCount || 0;
        acc.inProgressCount += row.inProgressCount || 0;
        acc.approvedCount += row.approvedCount || 0;
        acc.declinedCount += row.declinedCount || 0;
        acc.staffResponses += row.staffResponses || 0;
        acc.responseTotalMs += row.responseTotalMs || 0;
        return acc;
      },
      {
        advisers: 0,
        total: 0,
        newCount: 0,
        inProgressCount: 0,
        approvedCount: 0,
        declinedCount: 0,
        staffResponses: 0,
        responseTotalMs: 0,
      },
    );

    return {
      ...totals,
      completedCount: totals.approvedCount + totals.declinedCount,
      averageResponseMs:
        totals.staffResponses > 0
          ? totals.responseTotalMs / totals.staffResponses
          : 0,
    };
  }, [adviserStats]);

  return (
    <div className="fp-admin-layout">
      <Breadcrumb
        items={[
          {
            label: "Compliance",
            to: "/Department/Compliance",
          },
          { label: "Financial Promotions" },
        ]}
      />
      <div className="fp-admin-head">
        <h2 className="FP_H3">Financial Promotions</h2>

        <div className="fp-admin-filter-wrap">
          <PrimaryButton
            onClick={() => navigate("/FinancialPromotions/new")}
            label="New Submission"
          />

          <SearchField
            placeholder="Trading Style/AR/Content Type"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <FilterIcon className="fp-admin-filter-icon" />
        </div>
      </div>

      <div>
        <Tabs
          noShadow
          tabs={submissionTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === "renewals" && (
          <div style={{ margin: "16px 0" }}>
            <Banner type="warning" message={RENEWAL_NOTICE_MESSAGE} />
          </div>
        )}

        {activeTab === "stats" ? (
          <div className="fp-stats-section">
            <div className="fp-summary-grid">
              <div className="fp-summary-card fp-summary-card-blue">
                <span>Users</span>
                <strong>{statsTotals.advisers}</strong>
                <p>With submissions</p>
              </div>

              <div className="fp-summary-card fp-summary-card-cream">
                <span>Total</span>
                <strong>{statsTotals.total}</strong>
                <p>Excluding staff submissions</p>
              </div>

              <div className="fp-summary-card fp-summary-card-green">
                <span>Avg Staff Response</span>
                <strong>
                  {overviewLoading
                    ? "Loading..."
                    : formatAverageResponseTime(statsTotals.averageResponseMs)}
                </strong>
                <p>From user response to staff action</p>
              </div>

              <div className="fp-summary-card fp-summary-card-purple">
                <span>Completed</span>
                <strong>{statsTotals.completedCount}</strong>
                <p>Approved or Declined</p>
              </div>

              <div className="fp-summary-card fp-summary-card-pink">
                <span>In Progress</span>
                <strong>{statsTotals.inProgressCount}</strong>
                <p>In review / changes requested</p>
              </div>

              <div className="fp-summary-card fp-summary-card-teal">
                <span>New</span>
                <strong>{statsTotals.newCount}</strong>
                <p>Awaiting staff to review</p>
              </div>
            </div>

            <div className="fp-stats-card">
              <div className="fp-stats-card-head">
                <div>
                  <span className="fp-stats-label">Overview</span>
                  <h3>Financial Promotions by User</h3>
                  <p>
                    Average response time is calculated from submission or
                    re-upload to staff review, approval, decline, or requested
                    changes.
                  </p>
                </div>

                <div className="fp-stats-actions">
                  <div className="fp-stats-search">
                    <SearchField
                      placeholder="Search adviser or email"
                      value={statsSearchTerm}
                      onChange={(event) =>
                        setStatsSearchTerm(event.target.value)
                      }
                    />
                  </div>

                  <button
                    type="button"
                    className="fp-stats-reset-sort"
                    onClick={resetStatsSort}
                  >
                    Reset Sort
                  </button>
                </div>
              </div>

              <div className="fp-stats-table-wrap">
                <table className="fp-stats-table">
                  <thead>
                    <tr>
                      <th>
                        <StatsSortableHeader label="User" sortKey="adviser" />
                      </th>
                      <th>
                        <StatsSortableHeader label="Total" sortKey="total" />
                      </th>
                      <th>
                        <StatsSortableHeader label="New" sortKey="newCount" />
                      </th>
                      <th>
                        <StatsSortableHeader
                          label="In Progress"
                          sortKey="inProgressCount"
                        />
                      </th>
                      <th>
                        <StatsSortableHeader
                          label="Approved"
                          sortKey="approvedCount"
                        />
                      </th>
                      <th>
                        <StatsSortableHeader
                          label="Declined"
                          sortKey="declinedCount"
                        />
                      </th>
                      <th>
                        <StatsSortableHeader
                          label="Staff Responses"
                          sortKey="staffResponses"
                        />
                      </th>
                      <th>
                        <StatsSortableHeader
                          label="Avg Staff Response"
                          sortKey="averageResponseMs"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedAdviserStats.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="fp-admin-empty-cell">
                          {overviewLoading
                            ? "Loading stats..."
                            : "No stats found."}
                        </td>
                      </tr>
                    ) : (
                      sortedAdviserStats.map((row) => (
                        <tr key={row.adviserEmail}>
                          <td>
                            <div className="fp-stats-adviser">
                              <strong>
                                {row.adviserEmail || "Unknown User"}
                              </strong>
                            </div>
                          </td>

                          <td>
                            <strong>{row.total}</strong>
                          </td>

                          <td>
                            <span className="fp-stats-pill fp-stats-pill-new">
                              {row.newCount}
                            </span>
                          </td>

                          <td>
                            <span className="fp-stats-pill fp-stats-pill-progress">
                              {row.inProgressCount}
                            </span>
                          </td>

                          <td>
                            <span className="fp-stats-pill fp-stats-pill-approved">
                              {row.approvedCount}
                            </span>
                          </td>

                          <td>
                            <span className="fp-stats-pill fp-stats-pill-declined">
                              {row.declinedCount}
                            </span>
                          </td>

                          <td>
                            <strong>{row.staffResponses}</strong>
                          </td>

                          <td>
                            <strong>
                              {formatAverageResponseTime(row.averageResponseMs)}
                            </strong>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="fp-admin-table-wrap">
            <table className="fp-admin-table">
              <thead>
                <tr>
                  <th>
                    <div className="FP_Content"></div>
                  </th>
                  <th>
                    <div className="FP_Content">Adviser Details</div>
                  </th>

                  <th>
                    <div className="FP_Content">Content Type</div>
                  </th>

                  <th>
                    <div className="FP_Content">Total Submissions</div>
                  </th>

                  <th>
                    <div className="FP_Content">
                      {activeTab === "renewals" ? "Expiry Date" : activeTab === "completed"
                        ? "Approval At"
                        : "Submitted At"}
                    </div>
                  </th>

                  <th>
                    <div className="FP_Content">
                      {activeTab === "completed" ? "Status" : "Risk Detected"}
                    </div>
                  </th>

                  <th>
                    <div className="FP_Content">
                      {activeTab === "completed"
                        ? "Approved/Declined By"
                        : "Action Taken By"}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="fp-admin-empty-cell FP_Help_Text">
                      No submissions found for this view.
                    </td>
                  </tr>
                ) : (
                  paginatedSubmissions.map((submission, idx) => {
                    const detailsKey = getDetailsKey(submission);
                    const details = submissionDetailsById?.[detailsKey] || {};
                    const latestAttempt = details.latestAttempt || null;
                    const mediaTypes = details.mediaTypes || [];

                    const adviserMeta = latestAttempt?.input?.adviserMeta || {};

                    const allAttempts = Array.isArray(details.allAttempts)
                      ? details.allAttempts
                      : latestAttempt
                        ? [latestAttempt]
                        : [];

                    const adviserFullName =
                      [adviserMeta?.firstName, adviserMeta?.lastName]
                        .filter(Boolean)
                        .join(" ")
                        .trim() || "";

                    const riskLabel = getRiskLabel(submission);

                    const completedDecisionLabel = getCompletedDecisionLabel(
                      submission,
                      latestAttempt,
                    );

                    const primaryLabel =
                      submission?.title ||
                      submission?.companyName ||
                      submission?.firmName ||
                      submission?.adviserName ||
                      adviserMeta?.tradingName ||
                      adviserMeta?.tradingStyle ||
                      adviserFullName ||
                      "Adviser";

                    const secondaryLabel =
                      adviserMeta?.email ||
                      adviserMeta?.id ||
                      submission?.createdByEmail ||
                      submission?.userEmail ||
                      "-";

                    const secondaryLabel1 =
                      adviserMeta?.tradingName ||
                      adviserMeta?.tradingStyle ||
                      adviserFullName ||
                      "Adviser";

                    const adviserTag = adviserMeta?.isAR
                      ? "AR"
                      : adviserMeta?.fcaNumber
                        ? `FCA ${adviserMeta.fcaNumber}`
                        : "";

                    const reference =
                      submission?.reference ||
                      submission?.submissionReference ||
                      submission?.id;

                    const contentType =
                      mediaTypes.join(", ") ||
                      latestAttempt?.input?.mediaType ||
                      submission?.contentType ||
                      submission?.mediaType ||
                      "-";

                    const submittedValue =
                      submission?.createdAt ||
                      latestAttempt?.updatedAt ||
                      latestAttempt?.completedAt ||
                      latestAttempt?.createdAt;

                    const approvalDateValue =
                      submission?.approvedAt ||
                      latestAttempt?.approvedAt ||
                      latestAttempt?.reviewedAt ||
                      latestAttempt?.updatedAt ||
                      latestAttempt?.completedAt ||
                      submission?.updatedAt ||
                      submittedValue;

                    const attemptCount =
                      submission?.attemptCount ||
                      submission?.totalAttempts ||
                      allAttempts.length;

                    return (
                      <tr
                        key={detailsKey}
                        onClick={() =>
                          navigate(
                            `/FinancialPromotions/${encodeURIComponent(
                              String(submission.userEmail || "unknown"),
                            )}/${encodeURIComponent(
                              String(submission.id || ""),
                            )}`,
                          )
                        }
                      >
                        <td>
                          <div
                            className="fp-admin-adviser-main FP_Content"
                            style={{ marginBottom: "15px" }}
                          >
                            {(safePage - 1) * ROWS_PER_PAGE + idx + 1}.
                          </div>
                        </td>
                        <td>
                          <div
                            className="fp-admin-adviser-main FP_Body_Semibold"
                            style={{ marginBottom: "15px" }}
                          >
                            {primaryLabel}
                          </div>

                          <div className="fp-admin-adviser-sub FP_Help_Text">
                            {secondaryLabel}

                            {secondaryLabel1 &&
                            secondaryLabel1 !== secondaryLabel
                              ? ` • ${secondaryLabel1}`
                              : ""}

                            {adviserTag ? ` • ${adviserTag}` : ""}

                            <div
                              className="FP_Help_Text"
                              style={{ marginTop: "15px" }}
                            >
                              {String(reference)}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="FP_Content">{contentType}</div>
                        </td>

                        <td>
                          <div className="FP_Content">{attemptCount}</div>
                        </td>

                        <td>
                          <div className="FP_Content">
                            {formatSubmittedDateTime(
                              activeTab === "renewals"
                                ? submission.renewalExpiryDate
                                : activeTab === "completed"
                                ? approvalDateValue
                                : submittedValue,
                            )}
                          </div>
                        </td>

                        <td>
                          {activeTab === "completed" ? (
                            completedDecisionLabel === "-" ? (
                              <div className="FP_Content">-</div>
                            ) : (
                              <StatusTag status={completedDecisionLabel} />
                            )
                          ) : (
                            <RiskPill risk={riskLabel} />
                          )}
                        </td>

                        <td>
                          {(() => {
                            if (activeTab === "renewals") {
                              const approver = submission.renewalApprovedByEmail;
                              return (
                                <div className="FP_Content">
                                  {approver ? extractNameFromEmail(approver) : "-"}
                                </div>
                              );
                            }
                            const status = getEffectiveStatus(
                              submission,
                              latestAttempt,
                            );

                            if (activeTab === "new" && allAttempts.length > 1) {
                              const prevAttempt = allAttempts[1];

                              const prevRequestedBy =
                                prevAttempt?.requestedByEmail || "";

                              const prevStatus =
                                prevAttempt?.status ||
                                prevAttempt?.decision ||
                                "";

                              if (
                                normalizeStatus(prevStatus) ===
                                  "action_required" &&
                                prevRequestedBy
                              ) {
                                return (
                                  <div className="FP_Content">
                                    {extractNameFromEmail(prevRequestedBy)}
                                  </div>
                                );
                              }
                            }

                            if (
                              activeTab === "completed" &&
                              allAttempts.length > 0
                            ) {
                              const approvedAttempt = allAttempts.find((item) =>
                                [
                                  "approved",
                                  "declined",
                                  "rejected",
                                  "failed",
                                  "completed",
                                  "checked",
                                ].includes(
                                  normalizeStatus(
                                    item?.decision || item?.status || "",
                                  ),
                                ),
                              );

                              if (approvedAttempt) {
                                const approver =
                                  approvedAttempt?.approvedByEmail ||
                                  approvedAttempt?.reviewedByEmail ||
                                  approvedAttempt?.declinedByEmail ||
                                  approvedAttempt?.requestedByEmail ||
                                  approvedAttempt?.createdByEmail ||
                                  "";

                                if (approver) {
                                  return (
                                    <div className="FP_Content">
                                      {extractNameFromEmail(approver)}
                                    </div>
                                  );
                                }
                              }
                            }

                            const requestedBy =
                              submission?.requestedByEmail ||
                              latestAttempt?.requestedByEmail ||
                              "";

                            if (
                              normalizeStatus(status) === "action_required" &&
                              requestedBy
                            ) {
                              return (
                                <div className="FP_Content">
                                  {extractNameFromEmail(requestedBy)}
                                </div>
                              );
                            }

                            return <div className="FP_Content">-</div>;
                          })()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeTab !== "stats" && (
        <div className="fp-admin-pagination">
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
            maxVisiblePages={10}
          />
        </div>
      )}
    </div>
  );
}
